<!-- 配送规则、起送金额与配送范围的列表展示。表单弹窗和数据请求由父页面处理。 -->
<template>
  <section class="delivery-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span v-if="section === 'rules'">配送规则已接入后端真实列表与保存，金额统一按分维护。</span>
          <span v-else-if="section === 'min-amount'">起送金额当前复用配送规则里的起送门槛与免配送费阈值进行维护。</span>
          <span v-else-if="section === 'areas'">门店配送范围已接入真实列表、新增与删除，范围类型由后端统一保存。</span>
          <span v-else>无人机配送固定点位用于用户地址自动匹配最近可用配送点，并在小程序展示全部点位。</span>
        </div>
      </div>

      <div v-if="section === 'rules'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>规则名称</th>
              <th>适用门店</th>
              <th class="is-right">起送金额</th>
              <th class="is-right">基础配送费</th>
              <th class="is-right">每公里加价</th>
              <th>配送距离</th>
              <th class="is-right">免配送费门槛</th>
              <th>创建时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in deliveryRules" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ getStoreNames(item.storeId, item.storeName) }}</span></td>
              <td class="is-right">{{ formatAmount(item.minOrderAmount ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.baseFee ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.perKmFee ?? 0) }}</td>
              <td><span class="cell-text">{{ getDeliveryDistanceText(item) }}</span></td>
              <td class="is-right">{{ formatAmount(item.freeThreshold ?? 0) }}</td>
              <td>{{ item.createdAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑规则" @click="emit('editRule', item)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!deliveryRules.length && !loadingRules"><td colspan="9"><el-empty description="暂无配送规则" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'min-amount'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>规则名称</th>
              <th>适用门店</th>
              <th class="is-right">起送金额</th>
              <th class="is-right">免配送费门槛</th>
              <th class="is-right">基础配送费</th>
              <th>距离范围</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in deliveryRules" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ getStoreNames(item.storeId, item.storeName) }}</span></td>
              <td class="is-right">{{ formatAmount(item.minOrderAmount ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.freeThreshold ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.baseFee ?? 0) }}</td>
              <td><span class="cell-text">{{ getDeliveryDistanceText(item) }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="编辑门槛" @click="emit('editRule', item)"><el-icon><EditPen /></el-icon></button><button title="删除规则" @click="emit('removeRule', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!deliveryRules.length && !loadingRules"><td colspan="7"><el-empty description="暂无起送规则" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'areas'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>门店</th>
              <th>范围类型</th>
              <th>范围值</th>
              <th class="is-right">附加配送费</th>
              <th class="is-right">排序</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in deliveryAreas" :key="item.id">
              <td><span class="cell-text cell-text--wrap">{{ getStoreNames(item.storeId, item.storeName) }}</span></td>
              <td><span :class="['status-tag', toneClass(deliveryAreaTypeTone(item.type))]">{{ getDeliveryAreaTypeText(item.type) }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ getDeliveryAreaValueText(item) }}</span></td>
              <td class="is-right">{{ formatAmount(item.feeTier ?? 0) }}</td>
              <td class="is-right">{{ item.sort ?? 0 }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="删除范围" @click="emit('removeArea', item)"><el-icon><More /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!deliveryAreas.length && !loadingAreas"><td colspan="6"><el-empty description="暂无配送范围" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>配送点</th>
              <th>归属门店</th>
              <th>地址</th>
              <th>联系人</th>
              <th>坐标</th>
              <th class="is-right">服务半径</th>
              <th>状态</th>
              <th class="is-right">排序</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in deliveryPoints" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ getStoreNames(item.storeId, item.storeName) }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ item.address || '--' }}</span></td>
              <td><span class="cell-text">{{ formatContact(item) }}</span></td>
              <td><span class="cell-text">{{ formatPointLocation(item) }}</span></td>
              <td class="is-right">{{ item.serviceRadiusKm ?? 0 }}km</td>
              <td><span :class="['status-tag', toneClass(item.enabled === false ? 'muted' : 'success')]">{{ item.enabled === false ? '停用' : '启用' }}</span></td>
              <td class="is-right">{{ item.sort ?? 0 }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="查看位置" @click="emit('viewPointLocation', item)"><el-icon><Location /></el-icon></button>
                  <button title="编辑配送点" @click="emit('editPoint', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除配送点" @click="emit('removePoint', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!deliveryPoints.length && !loadingPoints"><td colspan="9"><el-empty description="暂无配送点" /></td></tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { Delete, EditPen, Location, More } from '@element-plus/icons-vue'
import type { DeliveryArea, DeliveryPoint, DeliveryRule } from '@/api/delivery'
import type { AdminStore } from '@/api/store'
import type { OperationStat } from '@/data/operationDesign'

type DeliverySection = 'rules' | 'min-amount' | 'areas' | 'points'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: DeliverySection
  stats: OperationStat[]
  deliveryRules: DeliveryRule[]
  deliveryAreas: DeliveryArea[]
  deliveryPoints: DeliveryPoint[]
  storeOptions: AdminStore[]
  loadingRules: boolean
  loadingAreas: boolean
  loadingPoints: boolean
  formatAmount: (amountInFen: number) => string
  getDeliveryDistanceText: (item: DeliveryRule) => string
  getDeliveryAreaTypeText: (type?: string) => string
  getDeliveryAreaValueText: (item: DeliveryArea) => string
  deliveryAreaTypeTone: (type?: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  editRule: [item: DeliveryRule]
  removeRule: [item: DeliveryRule]
  removeArea: [item: DeliveryArea]
  editPoint: [item: DeliveryPoint]
  removePoint: [item: DeliveryPoint]
  viewPointLocation: [item: DeliveryPoint]
}>()

const {
  section,
  stats,
  deliveryRules,
  deliveryAreas,
  deliveryPoints,
  storeOptions,
  loadingRules,
  loadingAreas,
  loadingPoints,
  formatAmount,
  getDeliveryDistanceText,
  getDeliveryAreaTypeText,
  getDeliveryAreaValueText,
  deliveryAreaTypeTone,
  toneClass,
} = toRefs(props)

const splitStoreValues = (value?: string) => (value ?? '').split(/[,\s，、]+/).map((item) => item.trim()).filter(Boolean)
const getStoreNames = (storeId?: string, storeName?: string): string => {
  const names = splitStoreValues(storeName)
  if (names.length > 1) return names.join('、')
  const ids = splitStoreValues(storeId)
  if (!ids.length) return storeName || '全部门店'
  return ids
    .map((id) => storeOptions.value.find((store) => store.id === id || store.code === id)?.name || id)
    .join('、')
}
const formatPointLocation = (item: DeliveryPoint): string => `${item.longitude}, ${item.latitude}`
const formatContact = (item: DeliveryPoint): string => [item.contactName, item.contactPhone].filter(Boolean).join(' ') || '--'
</script>

<style scoped lang="scss">
.delivery-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-text--wrap { max-width: 320px; overflow: visible; text-overflow: clip; white-space: normal; overflow-wrap: anywhere; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
